export enum DominantEnum {
  HOUSE = 'HOUSE',
  CONTRACT = 'CONTRACT',
  CLIENT = 'CLIENT',
  BOOKING = 'BOOKING',
  DEBT = 'DEBT',
  PAYMENT = 'PAYMENT'
}

export type AllConstructorType = {
  totalPages: number;
  currentPage: number;
  totalElements: number;

  data: {
    id: number;
    comment: string;
    createdBy: string;
    updatedDate: string;
    updatedTimestamp: number | null;
    dominant: DominantEnum;
  }[];
};

export type OneConstructorFieldType = {
  name: Record<string, string>;
  order: number;
  data: (number | string)[];
  customFieldId?: number;
  columnDominant: string;
};

export type ConstructorFilterKeys =
  | 'contractCreatedDateFilter'
  | 'contractCurrencyFilter'
  | 'contractPrepaymentDateFilter'
  | 'contractFormalityFilter'
  | 'contractResponsibleByFilter'
  | 'contractStatusFilter'
  | 'bookingStatusFilter'
  | 'bookingCreatedDateFilter'
  | 'clientDateFilter'
  | 'clientStatusFilter'
  | 'housesFilter'
  | 'flatRepairedFilter'
  | 'flatStatusFilter'
  | 'flatApartmentTypeFilter'
  | 'paymentCreatedDateFilter'
  | 'paymentResponsibleByFilter'
  | 'paymentTypeFilter'
  | 'paymentBaseFilter'
  | 'clientCountryFilter'
  | 'clientCityFilter'
  | 'clientDistrictFilter'
  | 'clientResponsibleByFilter'
  | 'paymentActiveFilter'
  | 'dateFilter'
  | 'buildingsFilter';

export type UploadConstructorContentType = 'select' | 'range-picker' | 'date';

export type ConstructorFormKeys =
  | 'contractCurrency'
  | 'clientCountry'
  | 'clientCity'
  | 'clientDistrict'
  | 'currency'
  | 'paymentDebit'
  | 'isActive'
  | 'isFormal'
  | 'flatRepaired'
  | 'contractResponsible'
  | 'contractStatus'
  | 'bookingStatus'
  | 'houses'
  | 'buildings'
  | 'flatStatus'
  | 'flatApartmentType'
  | 'clientResponsibleByFilter'
  | 'clientStatuses'
  | 'paymentResponsible'
  | 'paymentTypes'
  | 'paymentBase'
  | 'paymentCustomTypes'
  | 'buildings'
  | 'contractCreatedDateStart'
  | 'contractPrepaymentDateStart'
  | 'bookingCreatedDateStart'
  | 'paymentCreatedDateStart'
  | 'clientDateStart'
  | 'contractCreatedDateFinish'
  | 'contractPrepaymentDateFinish'
  | 'bookingCreatedDateFinish'
  | 'paymentCreatedDateFinish'
  | 'clientDateFinish';

export type UploadConstructorValueType = { label: string; required?: boolean; dominants: DominantEnum[] } & (
  | {
      key: 'select';
      options: {
        value: string | number | boolean | null;
        label: string;
      }[];
      childFormKey?: string;
      requiredFormKey?: string;
      formKey:
        | 'contractCurrency'
        | 'clientCountry'
        | 'clientCity'
        | 'clientDistrict'
        | 'currency'
        | 'paymentDebit'
        | 'isActive'
        | 'isFormal'
        | 'flatRepaired';
    }
  | {
      key: 'select';
      formKey:
        | 'contractResponsible'
        | 'contractStatus'
        | 'bookingStatus'
        | 'houses'
        | 'buildings'
        | 'flatStatus'
        | 'flatApartmentType'
        | 'clientResponsibleByFilter'
        | 'clientStatuses'
        | 'paymentResponsible'
        | 'paymentTypes'
        | 'paymentBase'
        | 'paymentCustomTypes'
        | 'buildings';
      multiple: true;
      childFormKey?: string;
      requiredFormKey?: string;
      options: {
        value: string | number | boolean | null;
        label: string;
      }[];
    }
  | {
      key: 'date';
      formKey: 'date';
    }
  | {
      key: 'range-picker';
      formKey: [
        (
          | 'contractCreatedDateStart'
          | 'contractPrepaymentDateStart'
          | 'bookingCreatedDateStart'
          | 'paymentCreatedDateStart'
          | 'clientDateStart'
        ),
        (
          | 'contractCreatedDateFinish'
          | 'contractPrepaymentDateFinish'
          | 'bookingCreatedDateFinish'
          | 'paymentCreatedDateFinish'
          | 'clientDateFinish'
        )
      ];
    }
);

export type ConstructorHeaderKeys =
  | 'contractNumberHeader'
  | 'contractPayNumberHeader'
  | 'contractCreatedDateHeader'
  | 'contractOldPriceHeader'
  | 'contractDealPriceHeader'
  | 'contractPrePaymentHeader'
  | 'contractMortgagePaymentHeader'
  | 'contractPayedAmountHeader'
  | 'contractResidualAmountHeader'
  | 'contractPayedPercentHeader'
  | 'contractCurrencyHeader'
  | 'contractDiscountPercentHeader'
  | 'contractDifferenceAmountFromOldPriceHeader'
  | 'contractPrepaymentDateHeader'
  | 'contractFormalityHeader'
  | 'contractResponsibleHeader'
  | 'contractStatusHeader'
  | 'bookingNumberHeader'
  | 'bookingStatusHeader'
  | 'bookingDealPriceHeader'
  | 'bookingDelayHeader'
  | 'bookingLeftUntilFinishedHeader'
  | 'bookingCreatedDate'
  | 'bookingResponsibleHeader'
  | 'clientFullNameHeader'
  | 'clientNumbersHeader'
  | 'clientCountryHeader'
  | 'clientCityHeader'
  | 'clientDistrictHeader'
  | 'clientAddressHeader'
  | 'clientResponsibleByHeader'
  | 'clientCreatedDateHeader'
  | 'clientStatusHeader'
  | 'houseNameHeader'
  | 'buildingNameHeader'
  | 'flatNumberHeader'
  | 'flatFloorHeader'
  | 'flatAreaHeader'
  | 'perAreaRepairedAmountHeader'
  | 'perAreaWithoutRepairAmountHeader'
  | 'flatPriceRepairedHeader'
  | 'flatPriceWithoutRepairHeader'
  | 'flatRepairedHeader'
  | 'flatStatusHeader'
  | 'flatApartmentTypeHeader'
  | 'flatLivingAreaHeader'
  | 'paymentPayNumberHeader'
  | 'paymentDateHeader'
  | 'paymentResponsibleByHeader'
  | 'paymentTypeHeader'
  | 'paymentBaseHeader'
  | 'prePaymentHeader'
  | 'monthlyPaymentHeader'
  | 'paymentAmountHeader'
  | 'paymentExchangeAmountHeader'
  | 'currencyExchangeAmountHeader'
  | 'paymentTitleHeader'
  | 'paymentDebitHeader'
  | 'paymentPayerHeader'
  | 'canceledDateHeader'
  | 'canceledNoteHeader'
  | 'paymentCurrencyHeader'
  | 'delayHeader'
  | 'totalDelayHeader'
  | 'debtDateHeader'
  | 'debtAmountHeader'
  | 'surchargeAmountHeader'
  | 'paymentPayerNumberHeader'
  | 'contractCustomFieldsHeader'
  | 'flatCustomFieldsHeader'
  | 'paymentCustomFieldsHeader'
  | 'contractDelayHeader';

export type OneConstructorType = {
  id: number;
  comment: string;
  dominant: DominantEnum;

  contractNumberHeader?: OneConstructorFieldType;
  contractPayNumberHeader?: OneConstructorFieldType;
  contractCreatedDateHeader?: OneConstructorFieldType;
  contractOldPriceHeader?: OneConstructorFieldType;
  contractDealPriceHeader?: OneConstructorFieldType;
  contractPrePaymentHeader?: OneConstructorFieldType;
  contractMortgagePaymentHeader?: OneConstructorFieldType;
  contractPayedAmountHeader?: OneConstructorFieldType;
  contractResidualAmountHeader?: OneConstructorFieldType;
  contractPayedPercentHeader?: OneConstructorFieldType;
  contractCurrencyHeader?: OneConstructorFieldType;
  contractDiscountPercentHeader?: OneConstructorFieldType;
  contractDifferenceAmountFromOldPriceHeader?: OneConstructorFieldType;
  contractPrepaymentDateHeader?: OneConstructorFieldType;
  contractFormalityHeader?: OneConstructorFieldType;
  contractResponsibleHeader?: OneConstructorFieldType;
  contractStatusHeader?: OneConstructorFieldType;
  bookingNumberHeader?: OneConstructorFieldType;
  bookingStatusHeader?: OneConstructorFieldType;
  bookingDealPriceHeader?: OneConstructorFieldType;
  bookingDelayHeader?: OneConstructorFieldType;
  bookingLeftUntilFinishedHeader?: OneConstructorFieldType;
  bookingCreatedDate?: OneConstructorFieldType;
  bookingResponsibleHeader?: OneConstructorFieldType;
  clientFullNameHeader?: OneConstructorFieldType;
  clientNumbersHeader?: OneConstructorFieldType;
  clientCountryHeader?: OneConstructorFieldType;
  clientCityHeader?: OneConstructorFieldType;
  clientDistrictHeader?: OneConstructorFieldType;
  clientAddressHeader?: OneConstructorFieldType;
  clientResponsibleByHeader?: OneConstructorFieldType;
  clientCreatedDateHeader?: OneConstructorFieldType;
  clientStatusHeader?: OneConstructorFieldType;
  houseNameHeader?: OneConstructorFieldType;
  buildingNameHeader?: OneConstructorFieldType;
  flatNumberHeader?: OneConstructorFieldType;
  flatFloorHeader?: OneConstructorFieldType;
  flatAreaHeader?: OneConstructorFieldType;
  perAreaRepairedAmountHeader?: OneConstructorFieldType;
  perAreaWithoutRepairAmountHeader?: OneConstructorFieldType;
  flatPriceRepairedHeader?: OneConstructorFieldType;
  flatPriceWithoutRepairHeader?: OneConstructorFieldType;
  flatRepairedHeader?: OneConstructorFieldType;
  flatStatusHeader?: OneConstructorFieldType;
  flatApartmentTypeHeader?: OneConstructorFieldType;
  flatLivingAreaHeader?: OneConstructorFieldType;
  paymentPayNumberHeader?: OneConstructorFieldType;
  paymentDateHeader?: OneConstructorFieldType;
  paymentResponsibleByHeader?: OneConstructorFieldType;
  paymentTypeHeader?: OneConstructorFieldType;
  paymentBaseHeader?: OneConstructorFieldType;
  prePaymentHeader?: OneConstructorFieldType;
  monthlyPaymentHeader?: OneConstructorFieldType;
  paymentAmountHeader?: OneConstructorFieldType;
  paymentExchangeAmountHeader?: OneConstructorFieldType;
  currencyExchangeAmountHeader?: OneConstructorFieldType;
  paymentTitleHeader?: OneConstructorFieldType;
  paymentDebitHeader?: OneConstructorFieldType;
  paymentPayerHeader?: OneConstructorFieldType;
  canceledDateHeader?: OneConstructorFieldType;
  canceledNoteHeader?: OneConstructorFieldType;
  paymentCurrencyHeader?: OneConstructorFieldType;
  delayHeader?: OneConstructorFieldType;
  totalDelayHeader?: OneConstructorFieldType;
  debtDateHeader?: OneConstructorFieldType;
  debtAmountHeader?: OneConstructorFieldType;
  surchargeAmountHeader?: OneConstructorFieldType;
  paymentPayerNumberHeader?: OneConstructorFieldType;
  contractDelayHeader?: OneConstructorFieldType;

  contractCreatedDateFilter?: boolean;
  contractCurrencyFilter?: boolean;
  contractPrepaymentDateFilter?: boolean;
  contractFormalityFilter?: boolean;
  contractResponsibleByFilter?: boolean;
  contractStatusFilter?: boolean;
  bookingStatusFilter?: boolean;
  bookingCreatedDateFilter?: boolean;
  clientCountryFilter?: boolean;
  clientCityFilter?: boolean;
  clientDistrictFilter?: boolean;
  clientResponsibleByFilter?: boolean;
  clientDateFilter?: boolean;
  clientStatusFilter?: boolean;
  housesFilter?: boolean;
  flatRepairedFilter?: boolean;
  flatStatusFilter?: boolean;
  flatApartmentTypeFilter?: boolean;
  paymentCreatedDateFilter?: boolean;
  paymentResponsibleByFilter?: boolean;
  paymentTypeFilter?: boolean;
  paymentBaseFilter?: boolean;
  paymentActiveFilter?: boolean;
};

export type ConstructorFilterFieldType = Record<`${string}${'Filter'}`, boolean>;
export type ConstructorHeaderFieldType = Record<`${string}${'Header'}`, OneConstructorFieldType>;

export type ConstructorFieldType = ConstructorHeaderFieldType & ConstructorFilterFieldType;
export type ConstructorWithHeaderFieldType = OneConstructorFieldType & {
  headerKey: `${string}${'Header'}`;
};
export type ConstructorFieldGroupType = Record<DominantEnum, ConstructorWithHeaderFieldType[]> &
  ConstructorFilterFieldType;

export type CreateOrUpdatePayloadType = {
  id?: number;
  comment: string;
  dominant: DominantEnum;
} & {
  [K in ConstructorHeaderKeys]: OneConstructorFieldType;
} & {
  [F in ConstructorFilterKeys]: boolean;
};

export type ConstructorCustomFieldType = {
  id: number;
  name: string;
  type: string;
} & Record<`name${string}`, string>;
